import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useFavorites } from "@/hooks/useFavorites";
import { Lightbox } from "@/components/Lightbox";
import { isVideo, heroOf } from "@/lib/media";
import { descStyle } from "@/lib/fonts";
import { PLACEHOLDER_IMAGE } from "@/lib/data";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  Heart, Star, MapPin, Phone, Globe, Instagram, Clock, Tag, Calendar, Home, Share2, UserPlus, Check, Play,
} from "lucide-react";
import { toast } from "sonner";

export default function BusinessProfile() {
  const { id } = useParams();
  const [biz, setBiz] = useState(null);
  const [lb, setLb] = useState(null);
  const { user } = useAuth();
  const { favs, follows, toggleFav, toggleFollow } = useFavorites();

  const loadBiz = () => api.get(`/businesses/${id}`).then((r) => setBiz(r.data)).catch(() => setBiz(false));
  useEffect(() => { loadBiz(); /* eslint-disable-next-line */ }, [id]);

  if (biz === false) return <div className="max-w-3xl mx-auto p-20 text-center font-display text-2xl">Business not found</div>;
  if (!biz) return <div className="p-20 text-center text-slate-400">Loading…</div>;

  const galleryImgs = (biz.gallery || []).filter(Boolean);
  const allMedia = (biz.media?.length ? biz.media : [biz.cover, ...galleryImgs]).filter(Boolean);
  const hero = heroOf(biz) || biz.cover;
  const heroIndex = Math.max(0, allMedia.indexOf(hero));
  const isFav = favs.includes(biz.id);
  const isFollowing = follows.includes(biz.id);
  const mapSrc = biz.lat && biz.lng
    ? `https://www.openstreetmap.org/export/embed.html?bbox=${biz.lng - 0.01}%2C${biz.lat - 0.008}%2C${biz.lng + 0.01}%2C${biz.lat + 0.008}&layer=mapnik&marker=${biz.lat}%2C${biz.lng}`
    : null;

  return (
    <div className="pb-16">
      {/* Cover / featured media */}
      <div className="relative h-64 md:h-96 cursor-pointer group bg-slate-900" onClick={() => hero && setLb({ images: allMedia, index: heroIndex })} data-testid="biz-cover-img">
        {isVideo(hero) ? (
          <video src={hero} className="w-full h-full object-cover" muted autoPlay loop playsInline />
        ) : (
          <img src={hero || PLACEHOLDER_IMAGE} alt={biz.name} className="w-full h-full object-cover" />
        )}
        <div className="absolute inset-0 bg-gradient-to-t from-slate-900/60 to-transparent" />
      </div>

      <div className="max-w-6xl mx-auto px-5 lg:px-8 -mt-20 relative">
        <div className="bg-white rounded-3xl shadow-xl border border-slate-100 p-6 md:p-8">
          <div className="flex flex-col md:flex-row gap-6 items-start">
            <img src={biz.logo || PLACEHOLDER_IMAGE} alt="" className="w-24 h-24 rounded-2xl object-cover border-4 border-white shadow-lg -mt-16 md:mt-0" />
            <div className="flex-1">
              <div className="flex flex-wrap items-center gap-2 mb-1">
                {biz.categories.map((c) => <Badge key={c} className="bg-[#F3F0EA] text-slate-700 rounded-full">{c}</Badge>)}
                {biz.home_based && <Badge className="bg-orange-100 text-orange-700 rounded-full"><Home className="w-3 h-3 mr-1" /> Home-based</Badge>}
              </div>
              <h1 className="font-display text-3xl md:text-4xl font-semibold tracking-tight">{biz.name}</h1>
              <div className="flex items-center gap-4 mt-2 text-slate-600 text-sm">
                {biz.rating != null ? (
                  <span className="flex items-center gap-1" data-testid="biz-rating"><Star className="w-4 h-4 fill-orange-400 text-orange-400" /> {biz.rating.toFixed(1)}{biz.review_count > 0 && <span className="text-slate-400">({biz.review_count})</span>}</span>
                ) : (
                  <span className="text-xs font-medium bg-teal-50 text-teal-600 px-2 py-0.5 rounded-full" data-testid="biz-rating-new">New · No reviews yet</span>
                )}
                <span className="flex items-center gap-1"><MapPin className="w-4 h-4" /> {biz.address}, {biz.city}</span>
              </div>
            </div>
            <div className="flex gap-2 w-full md:w-auto">
              <Button data-testid="profile-fav-btn" variant="outline" onClick={() => toggleFav(biz.id)} className="rounded-full flex-1 md:flex-none">
                <Heart className={`w-4 h-4 mr-1 ${isFav ? "fill-orange-500 text-orange-500" : ""}`} /> {isFav ? "Saved" : "Save"}
              </Button>
              <Button data-testid="profile-follow-btn" onClick={() => toggleFollow(biz.id)} className="rounded-full bg-orange-500 hover:bg-orange-600 flex-1 md:flex-none">
                {isFollowing ? <Check className="w-4 h-4 mr-1" /> : <UserPlus className="w-4 h-4 mr-1" />} {isFollowing ? "Following" : "Follow"}
              </Button>
              <Button variant="outline" size="icon" className="rounded-full" onClick={() => { navigator.clipboard?.writeText(window.location.href); toast.success("Link copied"); }}>
                <Share2 className="w-4 h-4" />
              </Button>
            </div>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mt-6">
          <div className="lg:col-span-2 space-y-6">
            <Card title="About">
              <div data-testid="biz-description" className="text-slate-600" style={descStyle(biz)}>{biz.description}</div>
            </Card>

            {biz.deals?.length > 0 && (
              <Card title="Current Deals">
                <div className="space-y-3">
                  {biz.deals.map((d) => (
                    <div key={d.id} data-testid={`deal-${d.id}`} className="flex items-start gap-3 bg-orange-50 rounded-2xl p-4">
                      <div className="w-10 h-10 rounded-xl bg-orange-500 flex items-center justify-center shrink-0"><Tag className="w-5 h-5 text-white" /></div>
                      <div>
                        <div className="flex items-center gap-2"><h4 className="font-semibold">{d.title}</h4>{d.discount && <Badge className="bg-orange-500 text-white rounded-full">{d.discount}</Badge>}</div>
                        <p className="text-sm text-slate-600">{d.description}</p>
                        {d.valid_until && <p className="text-xs text-slate-400 mt-1">Valid until {d.valid_until}</p>}
                      </div>
                    </div>
                  ))}
                </div>
              </Card>
            )}

            {biz.events?.length > 0 && (
              <Card title="Upcoming Events">
                <div className="space-y-3">
                  {biz.events.map((e) => (
                    <div key={e.id} data-testid={`event-${e.id}`} className="flex gap-4 items-center">
                      {e.image && <img src={e.image} alt="" className="w-20 h-20 rounded-2xl object-cover" />}
                      <div>
                        <div className="text-sm text-orange-600 font-medium flex items-center gap-1"><Calendar className="w-4 h-4" /> {e.date} · {e.price === 0 ? "Free" : `$${e.price}`}</div>
                        <h4 className="font-semibold">{e.title}</h4>
                        <p className="text-sm text-slate-500">{e.description}</p>
                      </div>
                    </div>
                  ))}
                </div>
              </Card>
            )}

            {allMedia.length > 1 && (
              <Card title="Gallery">
                <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                  {allMedia.map((g, i) => (
                    <div key={i} data-testid={`gallery-img-${i}`} onClick={() => setLb({ images: allMedia, index: i })}
                      className="relative aspect-square rounded-2xl overflow-hidden cursor-pointer hover:opacity-90 transition-opacity bg-slate-900">
                      {isVideo(g) ? (
                        <>
                          <video src={g} className="w-full h-full object-cover" muted />
                          <div className="absolute inset-0 flex items-center justify-center"><div className="w-10 h-10 rounded-full bg-black/50 flex items-center justify-center"><Play className="w-5 h-5 text-white" /></div></div>
                        </>
                      ) : (
                        <img src={g} alt="" className="w-full h-full object-cover" />
                      )}
                    </div>
                  ))}
                </div>
              </Card>
            )}

            <Reviews bizId={biz.id} user={user} onSubmitted={loadBiz} />
          </div>

          <div className="space-y-6">
            <Card title="Contact">
              <ul className="space-y-3 text-sm">
                {biz.phone && <li className="flex items-center gap-3"><Phone className="w-4 h-4 text-orange-500" /> {biz.phone}</li>}
                {biz.website && <li className="flex items-center gap-3"><Globe className="w-4 h-4 text-orange-500" /> <a href={biz.website} target="_blank" rel="noreferrer" className="text-orange-600 truncate">{biz.website.replace(/^https?:\/\//, "")}</a></li>}
                {biz.instagram && <li className="flex items-center gap-3"><Instagram className="w-4 h-4 text-orange-500" /> {biz.instagram}</li>}
                <li className="flex items-center gap-3"><MapPin className="w-4 h-4 text-orange-500" /> {biz.address}</li>
              </ul>
            </Card>

            {biz.hours && Object.keys(biz.hours).length > 0 && (
              <Card title="Hours">
                <ul className="space-y-1.5 text-sm">
                  {Object.entries(biz.hours).map(([day, h]) => (
                    <li key={day} className="flex justify-between"><span className="text-slate-500">{day}</span><span className="font-medium">{h}</span></li>
                  ))}
                </ul>
              </Card>
            )}

            {mapSrc && (
              <Card title="Location">
                <iframe title="map" src={mapSrc} className="w-full h-56 rounded-2xl border-0" data-testid="business-map" />
                <p className="text-sm text-slate-500 mt-2 flex items-center gap-1"><MapPin className="w-4 h-4" /> {biz.address}</p>
              </Card>
            )}
          </div>
        </div>
      </div>
      {lb && <Lightbox images={lb.images} index={lb.index} onClose={() => setLb(null)} />}
    </div>
  );
}

function Card({ title, children }) {
  return (
    <div className="bg-white rounded-3xl border border-slate-100 shadow-sm p-6">
      <h3 className="font-display text-xl font-semibold tracking-tight mb-4 flex items-center gap-2"><Clock className="hidden" />{title}</h3>
      {children}
    </div>
  );
}

function Reviews({ bizId, user, onSubmitted }) {
  const [reviews, setReviews] = useState([]);
  const [rating, setRating] = useState(0);
  const [hover, setHover] = useState(0);
  const [comment, setComment] = useState("");
  const [busy, setBusy] = useState(false);

  const load = () => api.get(`/businesses/${bizId}/reviews`).then((r) => setReviews(r.data)).catch(() => {});
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [bizId]);

  const mine = user && reviews.find((r) => r.user_id === user.id);
  useEffect(() => { if (mine) { setRating(mine.rating); setComment(mine.comment || ""); } }, [mine]);

  const submit = async () => {
    if (!user) return toast.error("Sign in to leave a review");
    if (!rating) return toast.error("Please pick a star rating");
    setBusy(true);
    try {
      await api.post(`/businesses/${bizId}/reviews`, { rating, comment });
      toast.success(mine ? "Review updated" : "Thanks for your review!");
      await load();
      onSubmitted && onSubmitted();
    } catch { toast.error("Could not submit review"); }
    finally { setBusy(false); }
  };

  return (
    <Card title={`Reviews${reviews.length ? ` (${reviews.length})` : ""}`}>
      {user ? (
        <div className="bg-[#FAF8F4] rounded-2xl p-4 mb-5" data-testid="review-form">
          <p className="text-sm font-medium text-slate-700 mb-2">{mine ? "Update your review" : "Leave a review"}</p>
          <div className="flex items-center gap-1 mb-3">
            {[1, 2, 3, 4, 5].map((n) => (
              <button key={n} type="button" data-testid={`review-star-${n}`}
                onMouseEnter={() => setHover(n)} onMouseLeave={() => setHover(0)} onClick={() => setRating(n)}
                className="p-0.5">
                <Star className={`w-6 h-6 transition-colors ${(hover || rating) >= n ? "fill-orange-400 text-orange-400" : "text-slate-300"}`} />
              </button>
            ))}
          </div>
          <textarea data-testid="review-comment" value={comment} onChange={(e) => setComment(e.target.value)}
            placeholder="Share your experience (optional)…" rows={3}
            className="w-full rounded-xl border border-slate-200 p-3 text-sm outline-none focus:border-orange-400 resize-none" />
          <Button data-testid="review-submit" onClick={submit} disabled={busy} className="rounded-full bg-orange-500 hover:bg-orange-600 mt-2">
            {busy ? "Submitting…" : mine ? "Update review" : "Submit review"}
          </Button>
        </div>
      ) : (
        <p className="text-sm text-slate-500 mb-4">Sign in to leave a review.</p>
      )}
      {reviews.length === 0 ? (
        <p className="text-sm text-slate-400">No reviews yet — be the first to share your experience.</p>
      ) : (
        <div className="space-y-4">
          {reviews.map((r) => (
            <div key={r.id} data-testid={`review-${r.id}`} className="border-b border-slate-100 pb-4 last:border-0">
              <div className="flex items-center justify-between">
                <span className="font-medium text-slate-800">{r.user_name}</span>
                <div className="flex items-center gap-0.5">
                  {[1, 2, 3, 4, 5].map((n) => (
                    <Star key={n} className={`w-4 h-4 ${r.rating >= n ? "fill-orange-400 text-orange-400" : "text-slate-200"}`} />
                  ))}
                </div>
              </div>
              {r.comment && <p className="text-sm text-slate-600 mt-1">{r.comment}</p>}
            </div>
          ))}
        </div>
      )}
    </Card>
  );
}
